
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 press-shrink",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm hover:shadow-md transition-all duration-200 shine-on-hover",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm hover:shadow-md transition-all duration-200",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground shadow-sm hover:shadow-md transition-all duration-200",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-sm hover:shadow-md transition-all duration-200",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        primary: "bg-primary text-white hover:bg-primary/90 shadow-sm hover:shadow-md transition-all duration-200 shine-on-hover",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type = "button", onClick, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    
    // Add a direct click handler that doesn't interfere with event propagation
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      console.log("Button component: Click detected", { 
        variant, 
        disabled: props.disabled,
        timestamp: Date.now(),
        target: e.currentTarget.dataset.testid || 'unknown'
      });
      
      // Call the original onClick handler if provided and button is not disabled
      if (onClick && !props.disabled) {
        // Don't prevent default or stop propagation here to allow events to bubble
        onClick(e);
        
        // Log after the callback to help with debugging
        console.log("Button component: onClick handler executed");
        
        // If this is a submit button with specific test IDs, dispatch a custom event
        if (e.currentTarget.dataset.testid === 'step-submit-button' || 
            e.currentTarget.dataset.testid === 'campaign-submit-button') {
          console.log("Button component: Dispatching global submit event");
          
          // Create a global event to ensure click is detected
          const submitEvent = new CustomEvent('global-submit-button-click', {
            bubbles: true,
            detail: { timestamp: Date.now(), testId: e.currentTarget.dataset.testid }
          });
          document.dispatchEvent(submitEvent);
        }
      }
    };
    
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        type={type}
        onClick={handleClick}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
